<template>
    <div class="space-y-4">
        <template v-for="field in approvalFields" :key="field.id">
            <!-- Text Display Field -->
            <div v-if="field.type === 'text'" class="space-y-2">
                <label 
                    :for="`approval_field_${field.id}`"
                    class="block text-sm opacity-80 mb-1.5"
                    :class="portalContentAndDesignStore.getPageContentTextColorClass"
                >
                    <span v-if="field.required" class="text-red-500">*</span>{{ field.title }}
                </label>
                <div 
                    :id="`approval_field_${field.id}`" 
                    class="text-sm"
                    :class="portalContentAndDesignStore.getPageContentTextColorClass"
                    v-html="field.text"
                >
                </div>
            </div>

            <!-- Input Field -->
            <div v-else-if="field.type === 'input'" class="space-y-2">
                <label 
                    :for="`approval_field_${field.id}`"
                    class="block text-sm opacity-80 mb-1.5"
                    :class="portalContentAndDesignStore.getPageContentTextColorClass"
                >
                    <span v-if="field.required" class="text-red-500">*</span>{{ field.title }}
                </label>
                <input
                    :id="`approval_field_${field.id}`"
                    type="text"
                    v-model="portalContentAndDesignStore.approvalFormData[`cfields[${field.id}]`]"
                    :required="field.required"
                    :class="portalContentAndDesignStore.getInputStyle"
                    @keydown.enter.prevent
                >
            </div>

            <!-- Textarea Field -->
            <div v-else-if="field.type === 'textarea'" class="space-y-2">
                <label 
                    :for="`approval_field_${field.id}`"
                    class="block text-sm opacity-80 mb-1.5"
                    :class="portalContentAndDesignStore.getPageContentTextColorClass"
                >
                    <span v-if="field.required" class="text-red-500">*</span>{{ field.title }}
                </label>
                <textarea
                    :id="`approval_field_${field.id}`"
                    v-model="portalContentAndDesignStore.approvalFormData[`cfields[${field.id}]`]"
                    :required="field.required"
                    :class="portalContentAndDesignStore.getInputStyle"
                    rows="3"
                    @keydown.enter.prevent
                >
                </textarea>
            </div>

            <!-- Select Field -->
            <div v-else-if="field.type === 'select'" class="space-y-2">
                <label 
                    :for="`approval_field_${field.id}`"
                    class="block text-sm opacity-80 mb-1.5"
                    :class="portalContentAndDesignStore.getPageContentTextColorClass"
                >
                    <span v-if="field.required" class="text-red-500">*</span>{{ field.title }}
                </label>
                <select
                    :id="`approval_field_${field.id}`"
                    v-model="portalContentAndDesignStore.approvalFormData[`cfields[${field.id}]`]"
                    :required="field.required"
                    :class="portalContentAndDesignStore.getInputStyle"
                    @keydown.enter.prevent
                >
                    <option v-for="(optionValue, optionKey) in field.options" :key="optionKey" :value="optionKey">
                        {{ optionValue }}
                    </option>
                </select>
            </div>

            <!-- Checkbox Field -->
            <div v-else-if="field.type === 'checkbox'" class="space-y-2">
                <label 
                    class="block text-sm opacity-80 mb-1.5"
                    :class="portalContentAndDesignStore.getPageContentTextColorClass"
                >
                    <span v-if="field.required" class="text-red-500">*</span>{{ field.title }}
                </label>
                <div class="space-y-2">
                    <template v-for="(optionValue, optionKey) in field.options" :key="optionKey">
                        <div class="space-x-2 flex text-sm">
                            <input 
                                type="checkbox" 
                                :id="`approval_field_${field.id}_${optionKey}`"
                                :value="optionKey"
                                v-model="portalContentAndDesignStore.approvalFormData[`cfields[${field.id}][${optionKey}]`]"
                                :class="portalContentAndDesignStore.getCheckboxInputStyle"
                            >
                            <label 
                                :for="`approval_field_${field.id}_${optionKey}`"
                                :class="portalContentAndDesignStore.getPageContentTextColorClass"
                            >
                                {{ optionValue }}
                            </label>
                        </div>
                    </template>
                </div>
            </div>

            <!-- Radio Field -->
            <div v-else-if="field.type === 'radio'" class="space-y-2">
                <label 
                    class="block text-sm opacity-80 mb-2"
                    :class="portalContentAndDesignStore.getPageContentTextColorClass"
                >
                    <span v-if="field.required" class="text-red-500">*</span>{{ field.title }}
                </label>
                <div class="space-y-2">
                    <template v-for="(optionValue, optionKey) in field.options" :key="optionKey">
                        <div class="space-x-2 flex text-sm">
                            <input 
                                type="radio" 
                                :id="`approval_field_${field.id}_${optionKey}`"
                                :name="`cfields[${field.id}]`"
                                :value="optionKey"
                                v-model="portalContentAndDesignStore.approvalFormData[`cfields[${field.id}]`]"
                                :required="field.required"
                                :class="portalContentAndDesignStore.getRadioInputStyle"
                            >
                            <label 
                                :for="`approval_field_${field.id}_${optionKey}`"
                                :class="portalContentAndDesignStore.getPageContentTextColorClass"
                            >
                                {{ optionValue }}
                            </label>
                        </div>
                    </template>
                </div>
            </div>

            <!-- Phone Field -->
            <div v-else-if="field.type === 'phone'" class="space-y-2">
                <label
                    :for="`approval_field_${field.id}`"
                    class="block text-sm opacity-80 mb-1.5"
                    :class="portalContentAndDesignStore.getPageContentTextColorClass"
                >
                    <span v-if="field.required" class="text-red-500">*</span>{{ field.title }}
                </label>
                <div class="flex gap-2">
                    <phone-dial-code-select
                        :model-value="getPhoneDialCode(field)"
                        @update:model-value="setPhoneDialCode(field, $event)"
                        :dial-codes="getAvailableDialCodes(field)"
                        :button-class="[portalContentAndDesignStore.getInputStyle, 'w-24 shrink-0']"
                    />
                    <input
                        type="tel"
                        :id="`approval_field_${field.id}`"
                        :value="getPhoneNumber(field)"
                        @input="setPhoneNumber(field, $event.target.value)"
                        :required="field.required"
                        :class="portalContentAndDesignStore.getInputStyle"
                        class="flex-1"
                        placeholder="Phone number"
                        @keydown.enter.prevent
                    >
                </div>
            </div>

            <!-- Countries Field -->
            <div v-else-if="field.type === 'countries'" class="space-y-2">
                <label 
                    :for="`approval_field_${field.id}`"
                    class="block text-sm opacity-80 mb-2"
                    :class="portalContentAndDesignStore.getPageContentTextColorClass"
                >
                    <span v-if="field.required" class="text-red-500">*</span>{{ field.title }}
                </label>
                <select
                    :id="`approval_field_${field.id}`"
                    v-model="portalContentAndDesignStore.approvalFormData[`cfields[${field.id}]`]"
                    :required="field.required"
                    :class="portalContentAndDesignStore.getInputStyle"
                    @keydown.enter.prevent
                >
                    <option value="">{{ portalContentAndDesignStore.translationSettings.portal_translation_general_select_country || 'Select a country' }}</option>
                    <option v-for="country in countries" :key="country.value" :value="country.value">
                        {{ country.name }}
                    </option>
                </select>
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { usePortalContentAndDesignStore } from "../../../../../../stores/partners/portalContentAndDesignStore";
import phoneDialCodesData from "../../../../../../helpers/phoneDialCodes";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const approvalFields = computed(() => {
    return portalContentAndDesignStore.approvalData?.approval_form_fields?.fields || [];
});

const countries = computed(() => {
    return portalContentAndDesignStore.approvalData?.countries || [];
});

const getAvailableDialCodes = (field) => {
    if (field.code_selection === 'selected' && field.selected_codes?.length) {
        return phoneDialCodesData.filter(dc => field.selected_codes.includes(dc.alpha2));
    }
    return phoneDialCodesData;
};

const getDefaultDialCode = (field) => {
    const codes = getAvailableDialCodes(field);
    if (codes.length === 1) return codes[0].dial_code;
    if (field.default_dial_code) return field.default_dial_code;
    try {
        const locale = navigator.language || '';
        const cc = locale.split('-')[1]?.toUpperCase();
        if (cc) {
            const match = phoneDialCodesData.find(dc => dc.alpha2 === cc);
            if (match) return match.dial_code;
        }
    } catch {}
    return '+1';
};

const getPhoneData = (field) => {
    const key = `cfields_phone_${field.id}`;
    if (!portalContentAndDesignStore.approvalFormData[key]) {
        portalContentAndDesignStore.approvalFormData[key] = {
            dial_code: getDefaultDialCode(field),
            number: '',
        };
    }
    return portalContentAndDesignStore.approvalFormData[key];
};

const getPhoneDialCode = (field) => getPhoneData(field).dial_code;
const getPhoneNumber = (field) => getPhoneData(field).number;

const setPhoneDialCode = (field, value) => {
    getPhoneData(field).dial_code = value;
};

const setPhoneNumber = (field, value) => {
    getPhoneData(field).number = value;
};
</script>
